import React, { useState, useEffect } from 'react';
import { supabase } from '../../../shared/lib/supabase';
import Button from '../../../shared/components/ui/Button';
import { MatchDetails } from '../../../shared/services/matchDetails';
import { useMobile } from '../../../shared/hooks/useMobile';
import { eventsCache, createCacheKey } from '../../../shared/utils/cacheUtils';
import { getStatIcon } from '../../../shared/utils/statIconUtils';

interface KeyEventsTabProps {
  matchDetails: MatchDetails;
  isActive?: boolean;
  onFetchKeyEvents?: () => void;
  isFetching?: boolean;
}

// Removed unused Player interface

interface KeyEvent {
  id: string;
  match_id: string;
  team_id: string | null;
  stat_type_id: string;
  match_minute: number | null; // Made nullable until timer issue is fixed
  primary_player_id: string | null;
  secondary_player_id: string | null;
  tags: any[];
  status: 'published' | 'unpublished';
  published_at: string | null;
  published_by: string | null;
  source_log_id: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  event_timestamp: string | null;
  is_key_event?: boolean; // For timeline events - indicates if this is also a key event
  action?: 'increment' | 'decrement'; // For timeline events - indicates increment or decrement
  
  // Joined data
  team?: {
    id: string;
    name: string;
    logo?: string;
  } | null;
  primary_player?: {
    id: string;
    full_name: string;
    kit_number?: number;
  } | null;
  secondary_player?: {
    id: string;
    full_name: string;
    kit_number?: number;
  } | null;
  stat_type?: {
    id: string;
    stat_key: string;
    display_name: string;
    category: string;
    event_result: string;
  } | null;
}

const KeyEventsTab: React.FC<KeyEventsTabProps> = ({ matchDetails, isActive = true }) => {
  // Debug: Log the match details received
  console.log('🚀 KeyEventsTab - matchDetails received:', matchDetails);
  console.log('🚀 KeyEventsTab - matchDetails.id:', matchDetails.id);
  console.log('🚀 KeyEventsTab - matchDetails.id type:', typeof matchDetails.id);
  console.log('🚀 KeyEventsTab - isActive:', isActive);
  console.log('🚀 KeyEventsTab - Component mounted!');

  const [events, setEvents] = useState<KeyEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'keyEvents' | 'allEvents'>('keyEvents');
  const { isMobile } = useMobile();
  // Removed unused state variables for view-only mode

  // Removed unused fetch functions for view-only mode

  // Function to fetch key events - only published events
  const fetchKeyEvents = async (isInitialLoad: boolean = false) => {
    if (!matchDetails.id) return;
    
    if (isInitialLoad) {
      setLoading(true);
    } else {
      setIsRefreshing(true);
    }
    setError(null);
    
    try {
      console.log('Fetching published key events for match:', matchDetails.id);
      
      // Optimized: Select only needed fields instead of * to reduce egress
      // Note: is_key_event doesn't exist in key_match_events table - all events here are key events
      const { data, error } = await supabase
        .from('key_match_events')
        .select(`
          id,
          match_id,
          team_id,
          stat_type_id,
          match_minute,
          primary_player_id,
          secondary_player_id,
          tags,
          status,
          published_at,
          published_by,
          source_log_id,
          is_active,
          created_at,
          updated_at,
          created_by,
          event_timestamp,
          team:teams(id, name, logo),
          primary_player:players!primary_player_id(id, full_name, kit_number),
          secondary_player:players!secondary_player_id(id, full_name, kit_number),
          stat_type:stat_types(id, stat_key, display_name, category, event_result)
        `)
        .eq('match_id', matchDetails.id)
        .eq('is_active', true)
        .eq('status', 'published') // Only fetch published events
        .order('event_timestamp', { ascending: false }); // Initial sort by timestamp

      if (error) {
        throw new Error(`Failed to fetch events: ${error.message}`);
      }

      console.log('Published events fetched successfully:', data?.length || 0);
      
      // Transform data to ensure joined fields are single objects, not arrays
      const transformedEvents: KeyEvent[] = (data || []).map((event: any) => ({
        id: event.id,
        match_id: event.match_id,
        team_id: event.team_id,
        stat_type_id: event.stat_type_id,
        match_minute: event.match_minute,
        primary_player_id: event.primary_player_id,
        secondary_player_id: event.secondary_player_id,
        tags: event.tags || [],
        status: event.status,
        published_at: event.published_at,
        published_by: event.published_by,
        source_log_id: event.source_log_id,
        is_active: event.is_active,
        created_at: event.created_at,
        updated_at: event.updated_at,
        created_by: event.created_by,
        event_timestamp: event.event_timestamp,
        is_key_event: true, // All events from key_match_events are key events
        // Ensure joined data is single objects, not arrays
        team: Array.isArray(event.team) ? (event.team[0] || null) : (event.team || null),
        primary_player: Array.isArray(event.primary_player) 
          ? (event.primary_player[0] || null) 
          : (event.primary_player || null),
        secondary_player: Array.isArray(event.secondary_player) 
          ? (event.secondary_player[0] || null) 
          : (event.secondary_player || null),
        stat_type: Array.isArray(event.stat_type) 
          ? (event.stat_type[0] || null) 
          : (event.stat_type || null),
      }));
      
      // Custom sorting: prioritize match_minute when available, otherwise use event_timestamp
      const sortedEvents = transformedEvents.sort((a, b) => {
        // Both events have match_minute - sort by match_minute (descending for latest first)
        if (a.match_minute !== null && b.match_minute !== null) {
          return b.match_minute - a.match_minute;
        }
        
        // Only event A has match_minute - it comes first
        if (a.match_minute !== null && b.match_minute === null) {
          return -1;
        }
        
        // Only event B has match_minute - it comes first
        if (a.match_minute === null && b.match_minute !== null) {
          return 1;
        }
        
        // Neither has match_minute - sort by event_timestamp (descending for latest first)
        const timeA = new Date(a.event_timestamp || a.created_at).getTime();
        const timeB = new Date(b.event_timestamp || b.created_at).getTime();
        return timeB - timeA;
      });
      
      setEvents(sortedEvents);
    } catch (err) {
      console.error('Error fetching key events:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch key events');
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      } else {
        setIsRefreshing(false);
      }
    }
  };

  // Function to fetch all timeline events
  const fetchTimelineEvents = async (isInitialLoad: boolean = false) => {
    if (!matchDetails.id) return;
    
    if (isInitialLoad) {
      setLoading(true);
    } else {
      setIsRefreshing(true);
    }
    setError(null);
    
    try {
      // Check cache first to reduce egress
      const cacheKey = createCacheKey('timeline_events', matchDetails.id);
      const cachedEvents = eventsCache.get(cacheKey) as KeyEvent[] | null;
      
      if (cachedEvents) {
        console.log('📦 Using cached timeline events for match:', matchDetails.id);
        setEvents(cachedEvents);
        if (isInitialLoad) {
          setLoading(false);
        } else {
          setIsRefreshing(false);
        }
        return;
      }
      
      console.log('🔄 Fetching timeline events for match:', matchDetails.id);
      
      // Optimized: Select only needed fields instead of * to reduce egress
      const { data, error } = await supabase
        .from('match_timeline')
        .select(`
          id,
          match_id,
          team_id,
          stat_type_id,
          match_minute,
          player_id,
          tags,
          is_key_event,
          source_log_id,
          is_active,
          action,
          created_at,
          updated_at,
          event_timestamp,
          team:teams(id, name, logo),
          player:players!player_id(id, full_name, kit_number),
          stat_type:stat_types(id, stat_key, display_name, category, event_result)
        `)
        .eq('match_id', matchDetails.id)
        .eq('is_active', true)
        .order('event_timestamp', { ascending: false });

      if (error) {
        throw new Error(`Failed to fetch timeline events: ${error.message}`);
      }

      console.log('Timeline events fetched successfully:', data?.length || 0);
      
      // Get status for key events from key_match_events table
      const keyEventIds = (data || [])
        .filter((e: any) => e.is_key_event && e.source_log_id)
        .map((e: any) => e.source_log_id);
      
      let keyEventStatuses: Record<string, 'published' | 'unpublished'> = {};
      if (keyEventIds.length > 0) {
        const { data: keyEvents } = await supabase
          .from('key_match_events')
          .select('source_log_id, status')
          .in('source_log_id', keyEventIds);
        
        if (keyEvents) {
          keyEventStatuses = keyEvents.reduce((acc: Record<string, 'published' | 'unpublished'>, ke: any) => {
            acc[ke.source_log_id] = ke.status as 'published' | 'unpublished';
            return acc;
          }, {});
        }
      }
      
      // Transform timeline events to KeyEvent format
      const transformedEvents: KeyEvent[] = (data || []).map((event: any) => ({
        id: event.id,
        match_id: event.match_id,
        team_id: event.team_id,
        stat_type_id: event.stat_type_id,
        match_minute: event.match_minute,
        primary_player_id: event.player_id || null, // Map player_id to primary_player_id
        secondary_player_id: null,
        tags: event.tags || [],
        status: event.is_key_event && event.source_log_id && keyEventStatuses[event.source_log_id]
          ? keyEventStatuses[event.source_log_id]
          : 'unpublished', // Fetch status from key_match_events if it's a key event
        published_at: null,
        published_by: null,
        source_log_id: event.source_log_id,
        is_active: event.is_active,
        created_at: event.created_at,
        updated_at: event.updated_at,
        created_by: null,
        event_timestamp: event.event_timestamp,
        is_key_event: event.is_key_event || false, // Include is_key_event flag
        action: event.action || 'increment', // Include action field (increment/decrement)
        // Joined data - ensure single objects, not arrays
        team: Array.isArray(event.team) ? (event.team[0] || null) : (event.team || null),
        primary_player: Array.isArray(event.player) 
          ? (event.player[0] ? {
              id: event.player[0].id,
              full_name: event.player[0].full_name,
              kit_number: event.player[0].kit_number
            } : null)
          : (event.player ? {
              id: event.player.id,
              full_name: event.player.full_name,
              kit_number: event.player.kit_number
            } : null),
        secondary_player: null,
        stat_type: Array.isArray(event.stat_type) 
          ? (event.stat_type[0] || null) 
          : (event.stat_type || null)
      }));
      
      // Custom sorting: prioritize match_minute when available, otherwise use event_timestamp
      const sortedEvents = transformedEvents.sort((a, b) => {
        if (a.match_minute !== null && b.match_minute !== null) {
          return b.match_minute - a.match_minute;
        }
        if (a.match_minute !== null && b.match_minute === null) {
          return -1;
        }
        if (a.match_minute === null && b.match_minute !== null) {
          return 1;
        }
        const timeA = new Date(a.event_timestamp || a.created_at).getTime();
        const timeB = new Date(b.event_timestamp || b.created_at).getTime();
        return timeB - timeA;
      });
      
      // Cache the sorted events to reduce future egress (30 second TTL)
      eventsCache.set(cacheKey, sortedEvents);
      console.log('💾 Cached timeline events for match:', matchDetails.id);
      
      setEvents(sortedEvents);
    } catch (err) {
      console.error('Error fetching timeline events:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch timeline events');
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      } else {
        setIsRefreshing(false);
      }
    }
  };

  // Removed fetchPlayers function for view-only mode

  // Helper function to format time display
  const formatTimeDisplay = (event: KeyEvent) => {
    if (event.match_minute !== null && event.match_minute !== undefined) {
      return `${event.match_minute}'`;
    }
    // If match_minute is null/undefined, show '--' (no fallback to event_timestamp)
    return '--';
  };

  // Fetch events when component mounts, tab is active, or viewMode changes
  useEffect(() => {
    if (isActive && matchDetails.id) {
      const isInitialLoad = events.length === 0;
      if (viewMode === 'keyEvents') {
        fetchKeyEvents(isInitialLoad);
      } else {
        fetchTimelineEvents(isInitialLoad);
      }
    }
  }, [isActive, matchDetails.id, viewMode]);

  // Removed all unused handler functions for view-only mode

  // Helper function to check if an event is a key event
  const isKeyEvent = (event: KeyEvent): boolean => {
    // In key events view, all events are key events
    if (viewMode === 'keyEvents') {
      return true;
    }
    // In all events view, check the is_key_event flag
    return event.is_key_event === true;
  };

  // Helper function to format player display
  const formatPlayerDisplay = (event: KeyEvent) => {
    const eventType = event.stat_type?.stat_key || '';
    const primaryPlayer = event.primary_player;
    const secondaryPlayer = event.secondary_player;
    
    if (!primaryPlayer && !secondaryPlayer) {
      return '';
    }
    
    // For substitution events
    if (eventType === 'substitution') {
      if (primaryPlayer && secondaryPlayer) {
        return `${primaryPlayer.full_name}${primaryPlayer.kit_number ? ` #${primaryPlayer.kit_number}` : ''} in for ${secondaryPlayer.full_name}${secondaryPlayer.kit_number ? ` #${secondaryPlayer.kit_number}` : ''}`;
      } else if (primaryPlayer) {
        return `${primaryPlayer.full_name}${primaryPlayer.kit_number ? ` #${primaryPlayer.kit_number}` : ''}`;
      } else if (secondaryPlayer) {
        return `${secondaryPlayer.full_name}${secondaryPlayer.kit_number ? ` #${secondaryPlayer.kit_number}` : ''}`;
      }
    }
    
    // For goal events
    if (eventType.includes('goal')) {
      if (primaryPlayer && secondaryPlayer) {
        return `Goal by ${primaryPlayer.full_name}${primaryPlayer.kit_number ? ` #${primaryPlayer.kit_number}` : ''} assist by ${secondaryPlayer.full_name}${secondaryPlayer.kit_number ? ` #${secondaryPlayer.kit_number}` : ''}`;
      } else if (primaryPlayer) {
        return `Goal by ${primaryPlayer.full_name}${primaryPlayer.kit_number ? ` #${primaryPlayer.kit_number}` : ''}`;
      } else if (secondaryPlayer) {
        return `Assist by ${secondaryPlayer.full_name}${secondaryPlayer.kit_number ? ` #${secondaryPlayer.kit_number}` : ''}`;
      }
    }
    
    // For other events with players
    if (primaryPlayer && secondaryPlayer) {
      return `${primaryPlayer.full_name}${primaryPlayer.kit_number ? ` #${primaryPlayer.kit_number}` : ''}, ${secondaryPlayer.full_name}${secondaryPlayer.kit_number ? ` #${secondaryPlayer.kit_number}` : ''}`;
    } else if (primaryPlayer) {
      return `${primaryPlayer.full_name}${primaryPlayer.kit_number ? ` #${primaryPlayer.kit_number}` : ''}`;
    } else if (secondaryPlayer) {
      return `${secondaryPlayer.full_name}${secondaryPlayer.kit_number ? ` #${secondaryPlayer.kit_number}` : ''}`;
    }
    
    return '';
  };

  // Get event icon
  // Use shared utility function for icons

  // Get event color
  const getEventColor = (eventType: string) => {
    switch (eventType) {
      case 'goal':
      case 'shot_inside_box_goal':
      case 'shot_outside_box_goal':
      case 'penalty_scored':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'penalty_missed':
      case 'red_card':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'yellow_card':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'substitution':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'save_open_play':
      case 'save_penalty':
        return 'text-purple-600 bg-purple-50 border-purple-200';
      case 'kick_off':
      case 'end_1st_half':
      case 'start_2nd_half':
      case 'full_time':
        return 'text-gray-600 bg-gray-50 border-gray-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  // Get short description for stat type
  const getShortDescription = (statKey: string) => {
    switch (statKey) {
      case 'shot_inside_box_goal':
        return 'Goal (Inside Box)';
      case 'shot_outside_box_goal':
        return 'Goal (Outside Box)';
      case 'penalty_scored':
        return 'Penalty Goal';
      case 'penalty_missed':
        return 'Penalty Missed';
      case 'yellow_card':
        return 'Yellow Card';
      case 'red_card':
        return 'Red Card';
      case 'substitution':
        return 'Substitution';
      case 'save_open_play':
        return 'Save (Open Play)';
      case 'save_penalty':
        return 'Save (Penalty)';
      case 'kick_off':
        return 'Kick Off';
      case 'end_1st_half':
        return 'End 1st Half';
      case 'start_2nd_half':
        return 'Start 2nd Half';
      case 'full_time':
        return 'Full Time';
      case 'own_goal':
        return 'Own Goal';
      case 'tackle_success':
        return 'Tackle Won';
      case 'foul_own_half':
        return 'Foul (Own Half)';
      case 'foul_opp_half':
        return 'Foul (Opp Half)';
      default:
        return statKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8 sm:py-12">
        <div className="flex items-center space-x-2 sm:space-x-3">
          <div className="w-5 h-5 sm:w-6 sm:h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <span className="text-sm sm:text-base text-gray-600 font-medium">Loading events...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 sm:py-12">
        <div className="text-red-600 mb-4 sm:mb-6">
          <svg className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">Error Loading Events</h3>
          <p className="text-sm sm:text-base text-gray-500 mb-4 sm:mb-6">{error}</p>
        </div>
        <Button
          onClick={() => {
            if (viewMode === 'keyEvents') {
              fetchKeyEvents();
            } else {
              fetchTimelineEvents();
            }
          }}
          variant="secondary"
          size={isMobile ? "sm" : "md"}
          icon={
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          }
        >
          Try Again
        </Button>
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="space-y-3 sm:space-y-4">
        {/* Header - Title with Toggle */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 sm:px-4 py-2 sm:py-3 relative">
          {/* Subtle loading overlay when refreshing */}
          {isRefreshing && (
            <div className="absolute inset-0 bg-white bg-opacity-50 rounded-lg flex items-center justify-center z-10">
              <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className="flex items-center space-x-1 sm:space-x-2">
                <span className="text-xs sm:text-sm font-medium text-gray-700">
                  {viewMode === 'keyEvents' ? 'KEY EVENTS:' : 'ALL EVENTS:'}
                </span>
                <div className="w-5 h-5 sm:w-6 sm:h-6 bg-blue-100 rounded flex items-center justify-center">
                  <svg className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <span className="text-xs sm:text-sm text-gray-600">with</span>
              </div>
              <div className="flex items-center space-x-1 sm:space-x-2">
                <span className="text-xs sm:text-sm font-medium text-gray-700">0</span>
                <span className="text-xs sm:text-sm text-gray-600">events</span>
              </div>
            </div>

            <div className="flex items-center space-x-2">
            {/* View Mode Toggle */}
            <div className="flex items-center bg-white border border-gray-300 rounded-md overflow-hidden">
              <button
                onClick={() => setViewMode('keyEvents')}
                disabled={isRefreshing}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  viewMode === 'keyEvents'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                } ${isRefreshing ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                Key Events
              </button>
              <button
                onClick={() => setViewMode('allEvents')}
                disabled={isRefreshing}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  viewMode === 'allEvents'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                } ${isRefreshing ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                All Events
              </button>
            </div>
            </div>
          </div>
        </div>

        {/* No Events Found Message */}
        <div className="text-center py-12 sm:py-16 bg-white rounded-xl border-2 border-dashed border-gray-300">
          <div className="text-gray-400 mb-3 sm:mb-4">
            <svg className="w-12 h-12 sm:w-16 sm:h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </div>
          <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">No Events Found</h3>
          <p className="text-sm sm:text-base text-gray-500 mb-4 sm:mb-6 px-4">No events available for this match. Events will appear here once they are synced from the scoresheet.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3 sm:space-y-4">
        {/* Header - Title with Toggle */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 sm:px-4 py-2 sm:py-3 relative">
          {/* Subtle loading overlay when refreshing */}
          {isRefreshing && (
            <div className="absolute inset-0 bg-white bg-opacity-50 rounded-lg flex items-center justify-center z-10">
              <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}
          <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 sm:space-x-3">
            <div className="flex items-center space-x-1 sm:space-x-2">
              <span className="text-xs sm:text-sm font-medium text-gray-700">
                {viewMode === 'keyEvents' ? 'KEY EVENTS:' : 'ALL EVENTS:'}
              </span>
              <div className="w-5 h-5 sm:w-6 sm:h-6 bg-blue-100 rounded flex items-center justify-center">
                <svg className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <span className="text-xs sm:text-sm text-gray-600">with</span>
            </div>
            <div className="flex items-center space-x-1 sm:space-x-2">
              <span className="text-xs sm:text-sm font-medium text-gray-900">{events.length} events recorded</span>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {/* View Mode Toggle */}
            <div className="flex items-center bg-white border border-gray-300 rounded-md overflow-hidden">
              <button
                onClick={() => setViewMode('keyEvents')}
                disabled={isRefreshing}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  viewMode === 'keyEvents'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                } ${isRefreshing ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                Key Events
              </button>
              <button
                onClick={() => setViewMode('allEvents')}
                disabled={isRefreshing}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  viewMode === 'allEvents'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                } ${isRefreshing ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                All Events
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Events List - Match Score Home Screen Style */}
      <div className="space-y-1">
        {events.map((event) => {
          const isEventKeyEvent = isKeyEvent(event);
          return (
            <div
              key={event.id}
              className={`bg-white border rounded-lg hover:bg-gray-50 transition-colors duration-150 touch-manipulation ${
                viewMode === 'allEvents' && isEventKeyEvent
                  ? 'border-blue-400 border-2 bg-blue-50'
                  : 'border-gray-200'
              }`}
            >
            <div className="px-3 sm:px-4 py-2 sm:py-3">
              <div className="flex items-center justify-between">
                {/* Left Side - Event Info */}
                <div className="flex items-center space-x-2 sm:space-x-3 flex-1 min-w-0">
                  {/* Event Icon - Smaller */}
                  <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded flex items-center justify-center text-xs sm:text-sm ${getEventColor(event.stat_type?.stat_key || '')}`}>
                    {getStatIcon(event.stat_type?.stat_key || '')}
                  </div>
                  
                  {/* Event Details - Compact */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-1 sm:space-x-2">
                      <span className="text-xs sm:text-sm font-medium text-gray-900">
                        {getShortDescription(event.stat_type?.stat_key || '')}
                        </span>
                      <span className="px-1.5 sm:px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded">
                        {formatTimeDisplay(event)}
                      </span>
                    </div>
                    
                    {/* Event Content - Single Line */}
                    <div className="flex items-center space-x-2 sm:space-x-4 mt-1 text-xs text-gray-600">
                      {event.team?.name && <span className="truncate">{event.team.name}</span>}
                      
                      {/* Players - Display Only */}
                      <span className="truncate">{formatPlayerDisplay(event)}</span>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        );
        })}
      </div>

      {/* Removed all modals for view-only mode */}
    </div>
  );
};

export default KeyEventsTab;